import { Component, OnInit, HostListener, ChangeDetectorRef, OnDestroy } from '@angular/core';
import { forkJoin } from 'rxjs';
import { ComponentWithErrorModal } from '../../base/component-with-error-modal';
import {
  BackgroundInfo,
  BackgroundInfoTopic,
  BackgroundInfoType,
  BackgroundInfoSubTopic
} from '../../../models';
import { NotificationService, BackgroundInfoService } from '../../../services';
import { ModalService } from '../../../modules/modal';
import { ComponentCanDeactivate } from 'src/app/shared/interfaces/component-can-deactivate.interface';

@Component({
  selector: 'base-background-info',
  template: ``,
})
export class BaseBackgroundInfoComponent
  extends ComponentWithErrorModal
  implements OnInit, OnDestroy, ComponentCanDeactivate {
  resourceId: number;
  jurisdictions: BackgroundInfo[] = [];
  activeJurisdiction = new BackgroundInfo();
  activeTopic: BackgroundInfoTopic;
  useCase: BackgroundInfoType;
  subTopics: BackgroundInfoSubTopic[] = [];
  preventPageChange = false;
  selectedSubTopicIndex = 0;
  isViewDestroyed = false;

  @HostListener('window:beforeunload', ['$event'])
  unloadNotification($event: any) {
    // if any unsaved changes, warn the user
    if (this.canDeactivate()) {
      $event.returnValue = true;
    }
  }

  constructor(
    protected notificationService: NotificationService,
    protected modalService: ModalService,
    protected backgroundInfoService: BackgroundInfoService,
    protected changeDetectionRef: ChangeDetectorRef
  ) {
    super(notificationService, modalService);
  }

  ngOnInit(): void {
    this.listenForErrorMessages();
    this.listenToGetBackgroundInfo();
  }

  ngOnDestroy(): void {
    this.isViewDestroyed = true;
    super.ngOnDestroy();
  }

  selectJurisdiction(jurisdiction: BackgroundInfo): void {
    this.activeJurisdiction = jurisdiction;
    this.selectFirstTopic();
  }

  isTopicSelected({ topicId }: BackgroundInfoTopic): boolean {
    return this.activeTopic.topicId === topicId;
  }

  selectTopic(topic = this.activeTopic): void {
    if (topic) {
      this.activeTopic = topic;
      this.backgroundInfoService
        .getBackgroundInfoSubTopics(topic, this.resourceId, this.useCase)
        .subscribe(async ({ data }) => {
          const getSubtopicObservables = [];

          for (const { backgroundInfoId } of data) {
            const observable = this.backgroundInfoService.getSubTopic(this.resourceId, backgroundInfoId);
            getSubtopicObservables.push(observable);
          }

          forkJoin(getSubtopicObservables).subscribe((responses) => {
            responses = responses.map(subTopic => ({ ...subTopic.data }));
            this.subTopics = responses;
            this.detectChanges();
            this.backgroundInfoService.emitSubTopicIndexChanged();
          });
        });
    } else {
      this.subTopics = [];
    }
  }

  preventPageChangeDueToUnSavedWork(shouldPrevent: boolean): void {
    this.preventPageChange = shouldPrevent;
  }

  canDeactivate(): boolean {
    return this.preventPageChange;
  }

  // must be public since it's used in template
  getBackgroundInfo(jurisdiction?: BackgroundInfo): void {
    this.backgroundInfoService.getBackgroundInfoData(this.resourceId, this.useCase).subscribe(({ data }) => {
      this.jurisdictions = this.filterOutJurisdictionWithoutTopics(data);
      if (jurisdiction) {
        this.selectJurisdiction(jurisdiction);
        const index =
          this.jurisdictions.findIndex(
            (j: BackgroundInfo) => {
              if (j && j.jurisdiction && jurisdiction && jurisdiction.jurisdiction) {
                return j.jurisdiction.countryId === jurisdiction.jurisdiction.countryId;
              } else {
                return false;
              }
            }
          );
        this.backgroundInfoService.changeJurisdictionByIndex(index);
      } else {
        this.activeJurisdiction = this.jurisdictions[0];
        this.selectFirstTopic();
      }
    });
  }

  protected filterOutJurisdictionWithoutTopics(backgroundInfos: BackgroundInfo[]): BackgroundInfo[] {
    return backgroundInfos.filter(({ topics }) => !!topics.length);
  }

  protected selectFirstTopic(): void {
    this.selectTopic(this.activeJurisdiction.topics ? this.activeJurisdiction.topics[0] : null);
  }

  protected resetSubTopicIndexIfNeeded(topic): void {
    if (JSON.stringify(topic) !== JSON.stringify(this.activeTopic)) {
      this.selectedSubTopicIndex = 0;
    }
  }

  protected listenToGetBackgroundInfo(): void {
    this.subscriptions.push(
      this.backgroundInfoService.getBackgroundInfo$.subscribe((jurisdiction: BackgroundInfo) => {
        this.getBackgroundInfo(jurisdiction);
      })
    );
  }

  protected detectChanges(): void {
    if (!this.isViewDestroyed) {
      this.changeDetectionRef.detectChanges();
    }
  }

  protected initializeBackgroundInfoAndGetData(): void {
    this.backgroundInfoService.initializeBackgroundInfo(this.resourceId, this.useCase).subscribe(() => {
      this.getBackgroundInfo();
    });
  }
}
